import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Download, Link2, Receipt as ReceiptIcon, FileText, CheckCircle2, ShieldCheck, Lock, Unlock } from "lucide-react";
import { formatMoney } from "@/lib/currency";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }); } catch { return s; }
}

const STATUS_STYLE = {
  active: "border-emerald-200 bg-emerald-50 text-emerald-700",
  upcoming: "border-sky-200 bg-sky-50 text-sky-700",
  grace: "border-amber-200 bg-amber-50 text-amber-800",
  expired: "border-rose-200 bg-rose-50 text-rose-700",
  completed: "border-slate-200 bg-slate-50 text-slate-700",
  cancelled: "border-slate-200 bg-slate-50 text-slate-500",
};

export default function LedgerDialog({ subscriberId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [busyPid, setBusyPid] = useState(null);
  const [reconcileTarget, setReconcileTarget] = useState(null);
  const [reconcileBankRef, setReconcileBankRef] = useState("");
  const [reconcileNote, setReconcileNote] = useState("");
  const [reconcileSubmitting, setReconcileSubmitting] = useState(false);
  const [unlockTarget, setUnlockTarget] = useState(null);

  const refresh = async () => {
    if (!subscriberId) return;
    try {
      const { data } = await api.get(`/subscribers/${subscriberId}/ledger`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    if (!open || !subscriberId) { setData(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/subscribers/${subscriberId}/ledger`);
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
        setData({ terms: [], payments: [] });
      }
    })();
  }, [open, subscriberId]);

  const downloadInvoice = async (paymentId) => {
    setBusyPid(paymentId);
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}/payments/${paymentId}/invoice.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); msg = formatApiError(j.detail) || msg; } catch { /* */ }
        toast.error(msg);
        return;
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `invoice-${paymentId}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      // Refresh ledger so invoice_number shows up
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyPid(null);
    }
  };

  const copyShareLink = async (paymentId) => {
    try {
      const { data } = await api.post(`/payments/${paymentId}/invoice/share-link`);
      try { await navigator.clipboard.writeText(data.url); toast.success(`Share link copied · valid ${data.expires_in_days}d`); }
      catch { toast.success(`Share link: ${data.url}`); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openReconcile = (payment) => {
    setReconcileTarget(payment);
    setReconcileBankRef(payment.upi_ref || payment.cheque_number || "");
    setReconcileNote("");
  };

  const submitReconcile = async () => {
    if (!reconcileTarget) return;
    setReconcileSubmitting(true);
    try {
      await api.post(`/payments/${reconcileTarget.id}/reconcile`, {
        bank_ref: reconcileBankRef || null,
        reconciliation_note: reconcileNote || null,
      });
      toast.success(`Reconciled · ${reconcileTarget.invoice_number || "invoice locked"}`);
      setReconcileTarget(null);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setReconcileSubmitting(false);
    }
  };

  const submitUnreconcile = async () => {
    if (!unlockTarget) return;
    try {
      await api.post(`/payments/${unlockTarget.id}/unreconcile`);
      toast.success("Reconciliation removed · invoice unlocked");
      setUnlockTarget(null);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ledger-dialog" className="max-w-4xl rounded-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptIcon size={18} className="text-[#002FA7]" />
            Subscription Ledger & Receipts
          </DialogTitle>
          <DialogDescription>
            {data?.display_name ? `${data.display_name} · ${data.subscriber_code}` : "Loading…"}
          </DialogDescription>
        </DialogHeader>

        {data === null ? (
          <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
        ) : (
          <div className="space-y-6">
            {/* Term timeline */}
            <div>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Term Timeline</div>
              <div className="rounded-sm border border-slate-200 bg-white">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plan</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.terms.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">No terms yet.</TableCell></TableRow>
                    )}
                    {data.terms.map((t) => (
                      <TableRow key={t.id} data-testid={`ledger-term-${t.id}`}>
                        <TableCell className="text-sm text-slate-900">{t.plan_name || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-600">{fmtDate(t.start_date)} → {fmtDate(t.end_date)}</TableCell>
                        <TableCell className="text-xs">
                          <span className="rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5">{t.renewal_type || "—"}</span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {t.amount_paid != null ? formatMoney(t.amount_paid, t.currency) : "—"}
                        </TableCell>
                        <TableCell className="font-mono text-[11px]">
                          {t.invoice_number ? (
                            <div className="flex items-center gap-1">
                              <span className="rounded-sm bg-slate-50 px-1.5 py-0.5 text-slate-700">{t.invoice_number}</span>
                              {t.reconciled_at && <Lock size={11} className="text-emerald-600" />}
                            </div>
                          ) : t.payment_id ? (
                            <span className="text-slate-400 italic">Click Download to mint</span>
                          ) : "—"}
                        </TableCell>
                        <TableCell>
                          <span className={`rounded-sm border px-2 py-0.5 text-[11px] ${STATUS_STYLE[t.effective_status] || "border-slate-200 bg-slate-50 text-slate-700"}`}>
                            {t.effective_status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {t.payment_id ? (
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="outline" onClick={() => downloadInvoice(t.payment_id)} disabled={busyPid === t.payment_id} data-testid={`ledger-download-${t.id}`} className="h-7 rounded-sm">
                                <Download size={12} className="mr-1" />{busyPid === t.payment_id ? "…" : "Download"}
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => copyShareLink(t.payment_id)} title="Copy share link" data-testid={`ledger-share-${t.id}`} className="h-7 w-7 rounded-sm text-[#002FA7] hover:bg-[#002FA7]/10">
                                <Link2 size={12} />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-[11px] text-slate-400">No payment</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Payments list */}
            <div>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Payments</div>
              <div className="rounded-sm border border-slate-200 bg-white">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Paid On</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Reconciled</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.payments.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">No payments recorded.</TableCell></TableRow>
                    )}
                    {data.payments.map((p) => {
                      const isReconciled = !!p.reconciled_at;
                      return (
                        <TableRow key={p.id} data-testid={`ledger-payment-${p.id}`} className={isReconciled ? "bg-emerald-50/40" : undefined}>
                          <TableCell className="text-xs text-slate-700">{fmtDate(p.paid_at)}</TableCell>
                          <TableCell className="text-sm">{p.method}</TableCell>
                          <TableCell className="font-mono text-[11px]">{p.upi_ref || p.cheque_number || "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{formatMoney(p.amount, p.currency)}</TableCell>
                          <TableCell className="font-mono text-[11px]">
                            {p.invoice_number || <span className="italic text-slate-400">—</span>}
                            {p.is_tax_invoice && <span className="ml-1 rounded-sm bg-emerald-50 px-1 py-0.5 text-[9px] text-emerald-700">GST</span>}
                          </TableCell>
                          <TableCell>
                            {isReconciled ? (
                              <div className="flex items-center gap-1.5">
                                <CheckCircle2 size={14} className="text-emerald-600" />
                                <div>
                                  <div className="text-[11px] font-medium text-emerald-700">{fmtDate(p.reconciled_at)}</div>
                                  {p.bank_ref && <div className="font-mono text-[9px] text-emerald-600/70">{p.bank_ref}</div>}
                                </div>
                              </div>
                            ) : (
                              <span className="text-[11px] text-slate-400">Pending</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="outline" onClick={() => downloadInvoice(p.id)} disabled={busyPid === p.id} data-testid={`ledger-pay-download-${p.id}`} className="h-7 rounded-sm">
                                <FileText size={12} className="mr-1" />{busyPid === p.id ? "…" : "PDF"}
                              </Button>
                              {isReconciled ? (
                                <Button size="icon" variant="ghost" title={`Unlock (reconciled by ${p.reconciled_by})`} onClick={() => setUnlockTarget(p)} data-testid={`ledger-unreconcile-${p.id}`} className="h-7 w-7 rounded-sm text-slate-500 hover:bg-slate-100">
                                  <Unlock size={12} />
                                </Button>
                              ) : (
                                <Button size="icon" variant="ghost" title="Mark Reconciled" onClick={() => openReconcile(p)} data-testid={`ledger-reconcile-${p.id}`} className="h-7 w-7 rounded-sm text-emerald-600 hover:bg-emerald-50">
                                  <ShieldCheck size={12} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Mark Reconciled */}
    <Dialog open={!!reconcileTarget} onOpenChange={(o) => !o && setReconcileTarget(null)}>
        <DialogContent data-testid="reconcile-dialog" className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <ShieldCheck size={18} /> Mark payment as Reconciled
            </DialogTitle>
            <DialogDescription>
              {reconcileTarget && (
                <>
                  This will <span className="font-semibold text-slate-700">lock the invoice from edits</span> and stamp a <span className="text-emerald-700 font-medium">RECONCILED</span> seal on every PDF download.
                  <div className="mt-2 rounded-sm border border-slate-200 bg-slate-50 p-2 text-xs">
                    <div className="font-medium text-slate-900">{formatMoney(reconcileTarget.amount, reconcileTarget.currency)} · {reconcileTarget.method}</div>
                    <div className="font-mono text-[10px] text-slate-500">{reconcileTarget.invoice_number || "(invoice will be minted)"}</div>
                  </div>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Bank Reference / UTR</Label>
              <Input
                data-testid="reconcile-bank-ref"
                value={reconcileBankRef}
                onChange={(e) => setReconcileBankRef(e.target.value)}
                placeholder="HDFC-TXN-12345"
                className="rounded-sm font-mono"
              />
              <p className="mt-1 text-[11px] text-slate-500">As seen on your bank statement (auto-filled from payment ref).</p>
            </div>
            <div>
              <Label className="text-sm">Note (optional)</Label>
              <Input
                data-testid="reconcile-note"
                value={reconcileNote}
                onChange={(e) => setReconcileNote(e.target.value)}
                placeholder="Matched bank statement 28-Apr-2026"
                className="rounded-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconcileTarget(null)} className="rounded-sm">Cancel</Button>
            <Button
              data-testid="reconcile-confirm"
              onClick={submitReconcile}
              disabled={reconcileSubmitting}
              className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <CheckCircle2 size={14} className="mr-1.5" />
              {reconcileSubmitting ? "Reconciling…" : "Confirm reconciliation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock confirm */}
      <Dialog open={!!unlockTarget} onOpenChange={(o) => !o && setUnlockTarget(null)}>
        <DialogContent data-testid="unreconcile-dialog" className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Unlock size={18} /> Unlock reconciled invoice?
            </DialogTitle>
            <DialogDescription>
              {unlockTarget && (
                <>
                  Removing reconciliation will let this payment be re-edited and removes the green seal from PDF downloads. Use only if the bank match was incorrect.
                  <div className="mt-2 rounded-sm border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                    Reconciled by <span className="font-medium">{unlockTarget.reconciled_by}</span> on <span className="font-medium">{fmtDate(unlockTarget.reconciled_at)}</span>
                    {unlockTarget.bank_ref && <div className="mt-1 font-mono text-[10px]">{unlockTarget.bank_ref}</div>}
                  </div>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockTarget(null)} className="rounded-sm">Cancel</Button>
            <Button
              data-testid="unreconcile-confirm"
              onClick={submitUnreconcile}
              className="rounded-sm bg-amber-600 text-white hover:bg-amber-700"
            >
              <Unlock size={14} className="mr-1.5" />Unlock invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
